/**
 * Debug-/simulatorpaneel (fase 1): alle events handmatig sturen + eventlog.
 * Ook de demo-scenario's uit de opdracht-minitests, als knoppen.
 */

import { h } from "./dom";
import type { App } from "./app";
import type { PhysicalSide, RallyReason, TeamId } from "../core";
import { candidateEv, manualEv, nextDetectorId, rallyEndEv, rallyStartEv, serveEv, sideSwitchEv, undoEv } from "../detect/simulated";

interface SimState {
  currentDetectorId: string | null;
}
const simState: SimState = { currentDetectorId: null };

export function buildDebugPanel(app: App): HTMLElement {
  const panel = h("details", { class: "card debug" });
  const summary = h("summary", {}, ["🛠 Simulator & eventlog (fase 1 debug)"]);
  // Let op: `App.render()` bewaart/herstelt de open-status rond de re-render
  // (het native `toggle`-event van <details> is asynchroon en komt te laat).

  const serveSide = h("select", {}, [
    h("option", { value: "left" }, ["Links"]),
    h("option", { value: "right" }, ["Rechts"]),
  ]);
  const serveConf = confSlider(0.95);
  const candWinner = h("select", {}, [
    h("option", { value: "A" }, ["Team A"]),
    h("option", { value: "B" }, ["Team B"]),
    h("option", { value: "none" }, ["Onduidelijk"]),
  ]);
  const candReason = h("select", {}, [
    h("option", { value: "ball_down_in_court" }, ["Bal in het veld"]),
    h("option", { value: "ball_out_after_last_touch" }, ["Bal uit (na laatste touch)"]),
    h("option", { value: "serve_fault" }, ["Opslagfout"]),
    h("option", { value: "net_or_contact_fault" }, ["Net-/contactfout"]),
    h("option", { value: "unknown" }, ["Onbekend"]),
  ]);
  const candConf = confSlider(0.9);

  function resetSim(): void {
    simState.currentDetectorId = null;
  }

  const logBox = h("pre", { class: "eventlog" }, ["(nog geen events)"]);

  const btn = (label: string, fn: () => void, cls = "btn small"): HTMLButtonElement =>
    h("button", { class: cls, onclick: fn }, [label]);

  const controls = h("div", { class: "sim-grid" }, [
    h("div", { class: "sim-block" }, [
      h("strong", {}, ["Opslag"])
      , h("div", { class: "inline" }, [
        btn("Serve ▶ (nieuwe rally)", () => {
          const id = nextDetectorId();
          simState.currentDetectorId = id;
          app.sim.emit(serveEv(serveSide.value as PhysicalSide, serveConf.value(), id));
        }),
      ]),
      h("label", { class: "inline small" }, ["zijde", serveSide, "conf", serveConf.wrap]),
    ]),
    h("div", { class: "sim-block" }, [
      h("strong", {}, ["Rally"]),
      btn("Start", () => simState.currentDetectorId && app.sim.emit(rallyStartEv(simState.currentDetectorId))),
      btn("Einde", () => simState.currentDetectorId && app.sim.emit(rallyEndEv(simState.currentDetectorId))),
    ]),
    h("div", { class: "sim-block" }, [
      h("strong", {}, ["Resultaat"]),
      h("div", { class: "inline" }, [
        btn("Verstuur", () => {
          const id = simState.currentDetectorId;
          if (!id) return;
          app.sim.emit(
            candidateEv(
              id,
              candWinner.value === "none" ? null : (candWinner.value as TeamId),
              candConf.value(),
              candReason.value as RallyReason,
              serveSide.value === "left" ? "left" : "right",
            ),
          );
        }),
      ]),
      h("label", { class: "inline small" }, ["winnaar", candWinner, "conf", candConf.wrap, "reden", candReason]),
    ]),
    h("div", { class: "sim-block" }, [
      h("strong", {}, ["Overig"]),
      h("div", { class: "inline" }, [
        btn("Punt A", () => app.emit(manualEv("A"))),
        btn("Punt B", () => app.emit(manualEv("B"))),
        btn("Bevestig", () => app.emit({ type: "RALLY_RESULT_CONFIRMED", confirmedBy: "user", ts: Date.now() })),
        btn("Ongedaan", () => app.emit(undoEv())),
        btn("Kantwissel ✓", () => app.emit(sideSwitchEv())),
        btn("Zijden ⇄", () => app.emit({ type: "SIDES_SWAP_MANUAL", ts: Date.now() })),
      ]),
    ]),
    h("div", { class: "sim-block" }, [
      h("strong", {}, ["Demo-scenario's (vanuit opdracht-minitests)"]),
      h("div", { class: "inline" }, [
        btn("Demo A→B (test 2)", () => demo2(app)),
        btn("Demo A→A (test 3)", () => demo3(app)),
        btn("Demo onzeker (test 4)", () => demo4(app)),
        btn("Demo correctie (test 5)", () => demo5(app)),
        btn("Reset sim", () => resetSim(), "btn small ghost"),
      ]),
    ]),
  ]);

  function refreshLog(): void {
    const evs = app.engine.events.slice(-14);
    logBox.textContent = evs
      .map((e) => {
        const rid = "detectorId" in e && e.detectorId ? `[${e.detectorId}]` : "";
        return `${String(e.ts).padEnd(14)} ${e.type} ${rid}`;
      })
      .join("\n");
    const st = app.engine.state;
    const info = [
      `phase=${st.phase} set=${st.setNumber} points=${st.currentSet.points.A}-${st.currentSet.points.B}`,
      `rally=${st.rally ? `${st.rally.id}(${st.rally.phase}/${st.rally.result?.status ?? "-"})` : "-"}`,
      `next=${st.nextServe ? `${st.nextServe.team}#${st.nextServe.playerIndex}` : "-"}`,
    ];
    logBox.append(document.createTextNode("\n\n" + info.join("  ")));
  }

  // Vul de log bij het bouwen (de app re-rendert na elke emit, dus elke build is vers)
  // én bij openen/klikken (de logbox overleeft dan evt. latere renderervaringen).
  refreshLog();
  summary.addEventListener("click", () => setTimeout(refreshLog, 50));
  panel.addEventListener("click", () => setTimeout(refreshLog, 30));

  panel.append(summary, controls, logBox);
  return panel;
}

/** Confidence-slider met zichtbare waarde; `.value()` leest de huidige waarde. */
function confSlider(value: number): { wrap: HTMLElement; value: () => number } {
  const out = h("span", { class: "small conf-out" }, [value.toFixed(2)]);
  const el = h("input", { type: "range", value: String(value), min: "0", max: "1", step: "0.01", style: "width:90px" });
  el.addEventListener("input", () => (out.textContent = Number(el.value).toFixed(2)));
  return {
    wrap: h("span", { class: "inline conf" }, [el, out]),
    value: () => Number(el.value),
  };
}

/* Demo-scenario's: exact de flow van de minitests in de opdracht. */
function demo2(app: App): void {
  const d1 = nextDetectorId();
  app.emit(serveEv("left", 0.95, d1));
  app.emit(rallyStartEv(d1));
  app.emit(rallyEndEv(d1));
  app.emit(candidateEv(d1, "B", 0.95, "ball_down_in_court", "right"));
  setTimeout(() => app.emit(serveEv("right", 0.95, nextDetectorId("s2"))), 1800);
}

function demo3(app: App): void {
  const d1 = nextDetectorId();
  app.emit(serveEv("left", 0.95, d1));
  app.emit(rallyStartEv(d1));
  app.emit(rallyEndEv(d1));
  app.emit(candidateEv(d1, "A", 0.95, "ball_down_in_court", "left"));
  setTimeout(() => app.emit(serveEv("left", 0.95, nextDetectorId("s2"))), 1800);
}

function demo4(app: App): void {
  const d1 = nextDetectorId();
  app.emit(serveEv("left", 0.95, d1));
  app.emit(rallyStartEv(d1));
  app.emit(rallyEndEv(d1));
  app.emit(candidateEv(d1, null, 0.4, "unknown", null));
  setTimeout(() => app.emit(serveEv("right", 0.95, nextDetectorId("s2"))), 1800);
}

function demo5(app: App): void {
  const d1 = nextDetectorId();
  app.emit(serveEv("left", 0.95, d1));
  app.emit(rallyStartEv(d1));
  app.emit(rallyEndEv(d1));
  app.emit(candidateEv(d1, "A", 0.95, "ball_down_in_court", "left"));
  setTimeout(() => app.emit(serveEv("right", 0.98, nextDetectorId("s2"))), 1800);
}
